import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { Color } from '../../_kernel/Color'
import { I18nText } from '../../_kernel/I18nText'
import { ImageUrlSet } from '../../_kernel/ImageUrlSet'
import { Language } from '../../shared/domain/Language'
import { ListOfI18nText } from '../../_kernel/ListOfI18nText'
import { Url } from '../../_kernel/Url'
import { Market } from '../../shared/domain/Market'
import { Device } from '../../shared/domain/Device'
import { BannerType, type ALLOWED_BANNER_TYPE } from './BannerType'
import { type ALLOWED_BANNER_SUB_TYPE, BannerSubtype } from './BannerSubtype'
import { DomainError } from '../../_kernel/DomainError'
import { ErrorsCode } from '../../_kernel/ErrorsCode'
import { type GETBannerInformationResponseType } from '../infrastructure/HTTPMarketingRepository/schemas/banners/banners-json'
import { type GETBannerInformationResponseSafeType } from '../infrastructure/HTTPMarketingRepository/schemas/banners'

export const ALLOWED_BANNER_SUBTYPES_BY_TYPE = {
  'billboard-banner': ['brand', 'campaign', 'member'],
} as Partial<Record<(typeof ALLOWED_BANNER_TYPE)[number], Partial<typeof ALLOWED_BANNER_SUB_TYPE>>>

export const BannerValidation = z.object({
  type: z.instanceof(BannerType, { message: '[Banner] type value is required' }),
  subtype: z.instanceof(BannerSubtype).optional(),
  market: z.instanceof(Market, { message: '[Banner] market value is required' }),
  language: z.instanceof(Language, { message: '[Banner] language is required' }),
  viewport: z.instanceof(Device).optional(),
  content: z.object({
    title: z.instanceof(ListOfI18nText, { message: '[Banner] title value is required' }),
    description: z.instanceof(I18nText).optional(),
    url: z.instanceof(Url).optional(),
    ctaText: z.instanceof(I18nText).optional(),
    bgColor: z.instanceof(Color).optional(),
    titleColor: z.instanceof(Color).optional(),
    descriptionColor: z.instanceof(Color).optional(),
    image: z.instanceof(ImageUrlSet).optional(),
  }),
})

export class Banner extends Model {
  static create({
    type,
    subtype,
    viewport,
    market,
    language,
    content,
  }: z.infer<typeof BannerValidation>) {
    BannerValidation.parse({ type, subtype, market, language, viewport, content })
    if (subtype) {
      const validSubtypes = ALLOWED_BANNER_SUBTYPES_BY_TYPE[type.value]
      if (!validSubtypes?.includes(subtype.value)) {
        throw DomainError.withCodes(ErrorsCode.INVALID_BANNER_SUBTYPE).withCauses(
          new Error(`Invalid subtype ${subtype.value} for type ${type.value}`)
        )
      }
    }
    return new Banner(type, market, language, content, subtype, viewport)
  }

  static createEmptyHeroBanner(market: Market, language: Language) {
    const type = BannerType.create({ value: 'hero-banner' })
    const content = {
      title: ListOfI18nText.create({
        list: [
          I18nText.create({ value: 'hero.long-stays' }),
          I18nText.create({ value: 'hero.hollidays' }),
          I18nText.create({ value: 'hero.adventures' }),
          I18nText.create({ value: 'hero.getaways' }),
          I18nText.create({ value: 'hero.discoveries' }),
          I18nText.create({ value: 'hero.dates' }),
          I18nText.create({ value: 'hero.kids' }),
        ],
      }),
      image: ImageUrlSet.fromDataset({
        image: {
          srcSet: [
            'https://static.stayforlong.com/web/images/banners/hero-banner-mobile.webp',
            'https://static.stayforlong.com/web/images/banners/hero-banner-tablet.webp',
            'https://static.stayforlong.com/web/images/banners/hero-banner-desktop.webp',
          ],
          alt: { default: 'Stayforlong Hero Banner' },
        },
      }),
    }

    return Banner.create({
      type,
      market,
      language,
      content,
    })
  }

  static fromDataset({
    info,
    market,
    language,
  }: {
    info: GETBannerInformationResponseType['banners'][number]
    market: Market
    language: Language
  }) {
    return Banner.#createFromApiData({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      info: { ...info, content: info.content.all! },
      market,
      language,
      useLegacyProcessing: true,
    })
  }

  static fromJSON(json: ReturnType<Banner['toJSON']>, market: Market, language: Language) {
    const { content } = json
    return new Banner(
      BannerType.create({ value: json.type }),
      market,
      language,
      {
        title: ListOfI18nText.fromJSON(content.title),
        ...(content.description && { description: I18nText.create(content.description) }),
        ...(content.url && { url: Url.fromJSON(content.url) }),
        ...(content.ctaText && { ctaText: I18nText.create(content.ctaText) }),
        ...(content.bgColor && { bgColor: Color.create(content.bgColor) }),
        ...(content.titleColor && { titleColor: Color.create(content.titleColor) }),
        ...(content.descriptionColor && {
          descriptionColor: Color.create(content.descriptionColor),
        }),
        ...(content.image && { image: ImageUrlSet.fromJSON(content.image) }),
      },
      BannerSubtype.createOrUndefined(json.subtype),
      json.viewport ? Device.create({ value: json.viewport }) : null
    )
  }

  static fromCurrentApiDataset({
    info,
    market,
    language,
  }: {
    info: GETBannerInformationResponseType['banners'][number]
    market: Market
    language: Language
  }) {
    return Banner.#createFromApiData({
      info,
      market,
      language,
      useLegacyProcessing: false,
    })
  }

  static #createFromApiData({
    info,
    market,
    language,
    useLegacyProcessing,
  }: {
    info: GETBannerInformationResponseSafeType[number]
    market: Market
    language: Language
    useLegacyProcessing: boolean
  }) {
    const { content } = info
    return Banner.create({
      type: BannerType.create({ value: info.type }),
      ...(info.subtype && { subtype: BannerSubtype.create({ value: info.subtype }) }),
      ...(info.viewport && { viewport: Device.create({ value: info.viewport }) }),
      market,
      language,
      content: {
        title: useLegacyProcessing
          ? ListOfI18nText.fromDataset({ list: content.title })
          : Banner.#processTitle(content.title),
        ...(content.description && {
          description: useLegacyProcessing
            ? I18nText.create({ value: content.description })
            : Banner.#processDescription(content.description),
        }),
        ...(content.url && { url: Url.fromJSON({ value: content.url }) }),
        ...(content.ctaText && {
          ctaText: useLegacyProcessing
            ? I18nText.create({ value: content.ctaText })
            : Banner.#processCtaText(content.ctaText),
        }),
        ...(content.bgColor && { bgColor: Color.create({ value: content.bgColor }) }),
        ...(content.titleColor && { titleColor: Color.create({ value: content.titleColor }) }),
        ...(content.descriptionColor && {
          descriptionColor: Color.create({ value: content.descriptionColor }),
        }),
        ...(content.image && {
          image: useLegacyProcessing
            ? ImageUrlSet.fromDataset({ image: content.image })
            : ImageUrlSet.fromCurrentApiDataset({ image: content.image }),
        }),
      },
    })
  }

  static #processTitle(title: string | string[] | Array<string | Partial<Record<string, string>>>) {
    if (Array.isArray(title)) {
      return ListOfI18nText.fromDataset({
        list: title.map(t => {
          if (typeof t === 'string') {
            return { default: t }
          }
          return t
        }),
      })
    }
    return ListOfI18nText.fromDataset({ list: [{ default: String(title) }] })
  }

  static #processDescription(description: string | string[] | Partial<Record<string, string>>) {
    if (typeof description === 'string') {
      return I18nText.create({ value: description })
    }
    if (Array.isArray(description)) {
      return I18nText.create({ value: description.join(' ') })
    }
    return I18nText.create({ value: description.default || '' })
  }

  static #processCtaText(ctaText: string | string[] | Partial<Record<string, string>>) {
    if (typeof ctaText === 'string') {
      return I18nText.create({ value: ctaText })
    }
    if (Array.isArray(ctaText)) {
      return I18nText.create({ value: ctaText.join(' ') })
    }
    return I18nText.create({ value: ctaText.default || '' })
  }

  constructor(
    public readonly _type: BannerType,
    private readonly _market: Market,
    private readonly _language: Language,
    public readonly _content: z.infer<typeof BannerValidation>['content'],
    public readonly _subtype?: BannerSubtype,
    public readonly _viewport?: Device | null
  ) {
    super()
  }

  get type() { return this._type } // prettier-ignore
  get subtype() { return this._subtype } // prettier-ignore
  get viewport() { return this._viewport ?? null } // prettier-ignore
  get content() { return this._content } // prettier-ignore
  get market() { return this._market } // prettier-ignore
  get language() { return this._language } // prettier-ignore

  toJSON() {
    const { content } = this
    return {
      type: this.type.value,
      ...(this.subtype && { subtype: this.subtype.value }),
      market: this.market.value,
      language: this.language.value,
      ...(this.viewport && { viewport: this.viewport.value }),
      content: {
        title: content.title.toJSON(),
        ...(content.description && { description: content.description.toJSON() }),
        ...(content.url && { url: content.url.toJSON() }),
        ...(content.ctaText && { ctaText: content.ctaText.toJSON() }),
        ...(content.bgColor && { bgColor: content.bgColor.toJSON() }),
        ...(content.titleColor && { titleColor: content.titleColor.toJSON() }),
        ...(content.descriptionColor && { descriptionColor: content.descriptionColor.toJSON() }),
        ...(content.image && { image: content.image.toJSON() }),
      },
    }
  }

  #getBanner() {
    const banner = this.content

    if (!banner) {
      return undefined
    }

    return banner
  }

  #isValidAnnouncementBanner() {
    return Boolean(this.content.title && this.content.title.list.length > 0)
  }

  #isValidBillboardBanner() {
    return Boolean(this.content.title && this.content.description)
  }

  #isValidBlockBanner() {
    return Boolean(
      this.content.title &&
        this.content.description &&
        this.content.ctaText &&
        this.content.url &&
        this.content.image
    )
  }

  #isValidHeroBanner() {
    return Boolean(this.content.title && this.content.image)
  }

  getAnnouncementBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidAnnouncementBanner()) {
      return undefined
    }

    return {
      title: banner.title.getListOfText(this.language.value),
      bgColor: banner.bgColor?.value,
      titleColor: banner.titleColor?.value,
    }
  }

  getBillboardBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidBillboardBanner()) {
      return undefined
    }

    return {
      title: banner.title.getListOfText(this.language.value),
      description: banner.description?.getText(this.language.value),
      url: banner.url?.value,
      ctaText: banner.ctaText?.getText(this.language.value),
      bgColor: banner.bgColor?.value,
      titleColor: banner.titleColor?.value,
      descriptionColor: banner.descriptionColor?.value,
      image: banner.image?.getTranslatedImage(this.language.value),
    }
  }

  getBlockBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidBlockBanner() || !banner.image) {
      return undefined
    }

    return {
      title: banner.title.getListOfText(this.language.value),
      description: banner.description?.getText(this.language.value),
      url: banner.url?.value,
      ctaText: banner.ctaText?.getText(this.language.value),
      image: banner.image?.getTranslatedImage(this.language.value),
    }
  }

  getHeroBannerInformation() {
    const banner = this.#getBanner()

    if (!banner || !this.#isValidHeroBanner()) {
      return undefined
    }

    const imagesByDevice = banner.image
      ? {
          srcSet: {
            mobile: banner.image.srcSet.at(0),
            tablet: banner.image.srcSet.at(1) ?? banner.image.srcSet.at(0),
            desktop:
              banner.image.srcSet.at(2) ?? banner.image.srcSet.at(1) ?? banner.image.srcSet.at(0),
          },
          alt: banner.image.alt.getText(this.language.value),
        }
      : undefined

    return {
      title: banner.title.getListOfText(this.language.value),
      image: imagesByDevice,
    }
  }

  getType() {
    return this.type.value
  }

  getSubtype() {
    return this.subtype?.value
  }
}
